var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defaultNotificationOptionsSettingValue } from '../../../../../../../backend/config/settings/NotificationOptionsDefaultValue';
import { useAlertConfig, useAlertUiTranslation } from '../../../../../../../ui/hooks';
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { ChipLight } from '@juvare/common-web-ui/ChipLight';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { Alert, Box, Typography } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import React from 'react';
import { useSelector } from 'react-redux';
import { channelIcon, channelText } from '../../../AlertFab/types';
import { useFormRow } from '../../Components/useFormRow';
export function useMessagesPanel(config) {
    const { t } = useAlertUiTranslation();
    const { notificationOptions, notificationRichTextMessageBody } = useAlertConfig();
    const userState = useUserState();
    const settings = useSelector((state) => (userState ? getSettings(state) : undefined));
    return [
        {
            type: 'form.panel',
            style: {
                layout: 'column',
                horizontalAlign: 'left',
                surface: 'level1',
            },
            children: [
                useFormRow([
                    {
                        type: 'form.content',
                        key: 'content',
                        template: {
                            data: '',
                            content: `<div style="display: flex; align-items: center"> <span class="icon-forward-to-inbox-outlined grey-700"></span> <p style="font-size: 20px; padding-left: 16px" class="subtitle1"> ${t('wizard.addMessage.messages')}</p></div>`,
                        },
                    },
                    {
                        type: 'form.panel',
                        style: {
                            paddingLeft: config.mode === 'edit' ? 56 : 49,
                        },
                        children: [
                            {
                                type: 'form.content',
                                key: 'content',
                                render: (handler) => {
                                    var _a, _b;
                                    return (React.createElement(React.Fragment, null,
                                        React.createElement(Box, { sx: { pb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 } },
                                            React.createElement(Typography, { variant: 'subtitle2' }, t('wizard.addMessage.shortMessage')),
                                            ChannelLabelWithIcon(handler, 'sms'),
                                            ChannelLabelWithIcon(handler, 'pager'),
                                            handler.formData.phin && ((_a = handler.formData.phinOptions) === null || _a === void 0 ? void 0 : _a.communicationSensitive) && (React.createElement(ChipLight, { label: 'Sensitive', color: 'warning' }))),
                                        handler.formData.phin && ((_b = handler.formData.phinOptions) === null || _b === void 0 ? void 0 : _b.communicationSensitive) && (React.createElement(Box, { pt: 1 },
                                            React.createElement(Alert, { severity: 'info' },
                                                React.createElement(Typography, { variant: 'subtitle2', sx: { fontWeight: 400 } }, t('wizard.addMessage.sensitiveCommunicationShortMessage', {
                                                    genericSensitiveMessageHint: notificationOptions &&
                                                        (notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.genericSensitiveMessageHint)
                                                        ? notificationOptions.genericSensitiveMessageHint
                                                        : defaultNotificationOptionsSettingValue.genericSensitiveMessageHint,
                                                })))))));
                                },
                                hidden: {
                                    custom: (handler) => {
                                        const channels = getMatchingChannels(handler);
                                        return !channels.some((channel) => channel === 'sms' || channel === 'pager');
                                    },
                                },
                            },
                            {
                                type: 'form.text',
                                label: t('wizard.addMessage.composeMessage'),
                                key: 'textMessage',
                                includeWhiteSpace: true,
                                event: {
                                    onChange: (value, handler) => {
                                        if (value && value.length > 1500) {
                                            const textWidget = handler.up().down('textMessage');
                                            if (textWidget) {
                                                const initalText = value.substring(0, 1500);
                                                textWidget.setData(initalText);
                                                textWidget.renderForm({ changed: true });
                                            }
                                        }
                                    },
                                },
                                validate: {
                                    required: true,
                                    requiredMessage: t('wizard.addMessage.messageRequired'),
                                },
                                rows: 4,
                                multiline: true,
                                hidden: {
                                    custom: (handler) => {
                                        const channels = getMatchingChannels(handler);
                                        return !channels.some((channel) => channel === 'sms' || channel === 'pager');
                                    },
                                },
                            },
                            {
                                type: 'form.content',
                                render: (handler) => {
                                    var _a, _b;
                                    return (React.createElement(Typography, { variant: 'caption', align: 'right', sx: {
                                            color: themeColors.text.subtle,
                                            width: '100%',
                                            display: 'inline-block',
                                        } }, t('wizard.addMessage.characterCount', {
                                        count: (_b = (_a = handler.formData.textMessage) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0,
                                    })));
                                },
                                hidden: {
                                    custom: (handler) => {
                                        const channels = getMatchingChannels(handler);
                                        return !channels.some((channel) => channel === 'sms' || channel === 'pager');
                                    },
                                },
                            },
                            {
                                type: 'form.divider',
                                key: 'longMessageDivider',
                                hidden: {
                                    custom: (handler) => {
                                        const channels = getMatchingChannels(handler);
                                        return !channels.some((channel) => channel === 'sms' || channel === 'pager');
                                    },
                                },
                            },
                            {
                                type: 'form.content',
                                key: 'content',
                                render: (handler) => {
                                    var _a, _b;
                                    return (React.createElement(React.Fragment, null,
                                        React.createElement(Box, { sx: { pb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 } },
                                            React.createElement(Typography, { variant: 'subtitle2' }, t('wizard.addMessage.longMessage')),
                                            ChannelLabelWithIcon(handler, 'email'),
                                            ChannelLabelWithIcon(handler, 'fax'),
                                            ChannelLabelWithIcon(handler, 'whatsapp'),
                                            ChannelLabelWithIcon(handler, 'inbox'),
                                            handler.formData.phin && ((_a = handler.formData.phinOptions) === null || _a === void 0 ? void 0 : _a.communicationSensitive) && (React.createElement(ChipLight, { label: 'Sensitive', color: 'warning' }))),
                                        handler.formData.phin && ((_b = handler.formData.phinOptions) === null || _b === void 0 ? void 0 : _b.communicationSensitive) && (React.createElement(Box, { pt: 1 },
                                            React.createElement(Alert, { severity: 'info' },
                                                React.createElement(Typography, { variant: 'subtitle2', sx: { fontWeight: 400 } }, t('wizard.addMessage.sensitiveCommunicationLongMessage', {
                                                    genericSensitiveMessageHint: notificationOptions &&
                                                        (notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.genericSensitiveMessageHint)
                                                        ? notificationOptions.genericSensitiveMessageHint
                                                        : defaultNotificationOptionsSettingValue.genericSensitiveMessageHint,
                                                })))))));
                                },
                                template: {
                                    data: '',
                                    content: '<div style="height: 40px;border-radius: 4px; padding: 8px; background: #E2F1FD; display: flex; align-items: center;">\n  <span class="icon-error-outline theme-accent-blue-bold" style="padding-right: 8px; padding-left: 8px"></span>\n  <p class="Color body2 theme-accent-blue-bold" style="font-size: 14px; font-weight: 500">“Inbox” is a required delivery method for PHIN direct communications.</p>\n</div>',
                                },
                                hidden: {
                                    custom: (handler) => {
                                        const channels = getMatchingChannels(handler);
                                        return !channels.some((channel) => channel === 'email' ||
                                            channel === 'fax' ||
                                            channel === 'inbox' ||
                                            channel === 'whatsapp');
                                    },
                                },
                            },
                            {
                                type: 'form.button',
                                key: 'pasteShortMessageToLongMessage',
                                icon: 'icon-clipboard-outlined',
                                label: t('widget.pasteShortMessage'),
                                event: {
                                    onClick: (handler) => {
                                        pasteShortMessage(handler, 'htmlMessage', t);
                                    },
                                },
                                hidden: {
                                    custom: (handler) => {
                                        const channels = getMatchingChannels(handler);
                                        const longMessageVisible = channels.some((channel) => channel === 'email' ||
                                            channel === 'fax' ||
                                            channel === 'inbox' ||
                                            channel === 'whatsapp');
                                        const shortMessageVisible = channels.some((channel) => channel === 'sms' || channel === 'pager');
                                        return !(longMessageVisible && shortMessageVisible);
                                    },
                                },
                            },
                            {
                                type: 'alert.longMessage',
                                key: 'htmlMessage',
                                label: '',
                                defaultValue: notificationRichTextMessageBody || '',
                                validate: {
                                    requiredMessage: t('wizard.addMessage.messageRequired'),
                                    custom: (value, handler) => {
                                        var _a;
                                        if (!value && ((_a = handler.formData) === null || _a === void 0 ? void 0 : _a.channels)) {
                                            return handler.formData.channels.some((channel) => channel === 'email' ||
                                                channel === 'fax' ||
                                                channel === 'inbox' ||
                                                channel === 'whatsapp')
                                                ? t('wizard.addMessage.messageRequired')
                                                : undefined;
                                        }
                                        return;
                                    },
                                },
                                hidden: {
                                    custom: (handler) => {
                                        const channels = getMatchingChannels(handler);
                                        return !channels.some((channel) => channel === 'email' ||
                                            channel === 'fax' ||
                                            channel === 'inbox' ||
                                            channel === 'whatsapp');
                                    },
                                },
                            },
                            {
                                type: 'form.divider',
                                key: 'voiceMessageDivider',
                                hidden: {
                                    custom: (handler) => {
                                        const channels = getMatchingChannels(handler);
                                        return !channels.some((channel) => channel === 'voice');
                                    },
                                },
                            },
                            {
                                type: 'form.panel',
                                hidden: {
                                    custom: (handler) => {
                                        const channels = getMatchingChannels(handler);
                                        return !channels.some((channel) => channel === 'voice');
                                    },
                                },
                                clearDataOnHide: true,
                                children: [
                                    {
                                        type: 'alert.voiceMessageTypeLayout',
                                        children: [
                                            {
                                                type: 'form.content',
                                                key: 'voiceMessageContent',
                                                style: { width: 585 },
                                                render: (handler) => {
                                                    var _a;
                                                    return (React.createElement(Box, { sx: {
                                                            pb: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flexWrap: 'wrap',
                                                            gap: 1,
                                                        } },
                                                        React.createElement(Typography, { variant: 'subtitle2' }, t('wizard.addMessage.voiceMessage')),
                                                        ChannelLabelWithIcon(handler, 'voice'),
                                                        handler.formData.phin &&
                                                            ((_a = handler.formData.phinOptions) === null || _a === void 0 ? void 0 : _a.communicationSensitive) && (React.createElement(ChipLight, { label: 'Sensitive', color: 'warning' }))));
                                                },
                                            },
                                            {
                                                type: 'form.radio',
                                                key: 'voiceMessageType',
                                                style: {
                                                    align: 'left',
                                                    inline: true,
                                                },
                                                values: [
                                                    {
                                                        label: t('wizard.addMessage.convertTextToVoice'),
                                                        value: 'textToVoice',
                                                    },
                                                    {
                                                        label: t('wizard.addMessage.recordOrUpload'),
                                                        value: 'record',
                                                    },
                                                ],
                                                defaultValue: 'textToVoice',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'form.button',
                                        key: 'pasteShortMessageToVoice',
                                        icon: 'icon-clipboard-outlined',
                                        label: t('widget.pasteShortMessage'),
                                        event: {
                                            onClick: (handler) => {
                                                pasteShortMessage(handler, 'voiceMessage', t);
                                            },
                                        },
                                        hidden: {
                                            custom: (handler) => {
                                                const channels = getMatchingChannels(handler);
                                                const voiceComposeMessageVisible = handler.up().getData().voiceMessageType == 'textToVoice';
                                                const shortMessageVisible = channels.some((channel) => channel === 'sms' || channel === 'pager');
                                                return !(voiceComposeMessageVisible && shortMessageVisible);
                                            },
                                        },
                                    },
                                    {
                                        type: 'form.content',
                                        key: 'voiceMessageContent',
                                        render: (handler) => {
                                            var _a;
                                            return (React.createElement(React.Fragment, null, handler.formData.phin && ((_a = handler.formData.phinOptions) === null || _a === void 0 ? void 0 : _a.communicationSensitive) && (React.createElement(Box, { pt: 1 },
                                                React.createElement(Alert, { severity: 'info' },
                                                    React.createElement(Typography, { variant: 'subtitle2', sx: { fontWeight: 400 } }, t('wizard.addMessage.sensitiveCommunicationVoiceMessage')))))));
                                        },
                                    },
                                    {
                                        type: 'form.text',
                                        key: 'voiceMessage',
                                        includeWhiteSpace: true,
                                        rows: 4,
                                        label: t('wizard.addMessage.composeMessage'),
                                        event: {
                                            onChange: (value, handler) => {
                                                if (value && value.length > 1500) {
                                                    const textWidget = handler.up().down('voiceMessage');
                                                    if (textWidget) {
                                                        const initalText = value.substring(0, 1500);
                                                        textWidget.setData(initalText);
                                                        textWidget.renderForm({ changed: true });
                                                    }
                                                }
                                            },
                                        },
                                        hidden: {
                                            custom: (handler) => {
                                                const voiceMessageOption = handler.up().getData().voiceMessageType;
                                                return voiceMessageOption !== 'textToVoice';
                                            },
                                        },
                                        validate: {
                                            required: true,
                                            requiredMessage: t('wizard.addMessage.messageRequired'),
                                        },
                                        multiline: true,
                                    },
                                    {
                                        type: 'form.content',
                                        render: (handler) => {
                                            var _a, _b;
                                            return (React.createElement(Typography, { variant: 'caption', align: 'right', sx: {
                                                    color: themeColors.text.subtle,
                                                    width: '100%',
                                                    display: 'inline-block',
                                                } }, t('wizard.addMessage.characterCount', {
                                                count: (_b = (_a = handler.formData.voiceMessage) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0,
                                            })));
                                        },
                                        hidden: {
                                            custom: (handler) => {
                                                const voiceMessageOption = handler.up().getData().voiceMessageType;
                                                return voiceMessageOption !== 'textToVoice';
                                            },
                                        },
                                    },
                                    {
                                        type: 'alert.recordMessage',
                                        key: 'voiceRecordings',
                                        label: t('wizard.addMessage.recordMessage'),
                                        hidden: {
                                            custom: (handler) => {
                                                const voiceMessageOption = handler.up().getData().voiceMessageType;
                                                return voiceMessageOption !== 'record';
                                            },
                                        },
                                        validate: {
                                            required: true,
                                        },
                                    },
                                ],
                            },
                            {
                                type: 'alert.voiceOptionsLayout',
                                hidden: {
                                    custom: (handler) => {
                                        const channels = getMatchingChannels(handler);
                                        return !channels.some((channel) => channel === 'voice');
                                    },
                                },
                                children: [
                                    {
                                        type: 'form.select',
                                        key: 'voiceOptions.repeatTimes',
                                        label: t('wizard.addMessage.repeatCall'),
                                        valueProvider: {
                                            type: 'classifier.repeatCall',
                                        },
                                        defaultValue: notificationOptions &&
                                            (notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.voiceRepeatOptions) &&
                                            'voiceRepeatCall' in notificationOptions.voiceRepeatOptions
                                            ? notificationOptions.voiceRepeatOptions.voiceRepeatCall
                                            : defaultNotificationOptionsSettingValue.voiceRepeatOptions.voiceRepeatCall,
                                    },
                                    {
                                        type: 'form.select',
                                        key: 'voiceOptions.waitTime',
                                        label: t('wizard.addMessage.callInterval'),
                                        hidden: {
                                            custom: (handler) => {
                                                var _a;
                                                if ((_a = handler.formData) === null || _a === void 0 ? void 0 : _a.voiceOptions) {
                                                    const data = handler.formData.voiceOptions.repeatTimes;
                                                    return data === 0 || !data;
                                                }
                                                return true;
                                            },
                                        },
                                        valueProvider: {
                                            type: 'classifier.callInterval',
                                        },
                                        clearDataOnHide: true,
                                        defaultValue: notificationOptions &&
                                            (notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.voiceRepeatOptions) &&
                                            'voiceRepeatDelay' in (notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.voiceRepeatOptions)
                                            ? notificationOptions.voiceRepeatOptions.voiceRepeatDelay
                                            : defaultNotificationOptionsSettingValue.voiceRepeatOptions.voiceRepeatDelay,
                                    },
                                ],
                            },
                        ],
                    },
                ], config.mode),
            ],
        },
        {
            type: 'form.panel',
            style: {
                layout: 'column',
                horizontalAlign: 'left',
                surface: 'level1',
            },
            children: [
                useFormRow([
                    {
                        type: 'form.content',
                        key: 'content',
                        template: {
                            data: '',
                            content: `<div style="display: flex; align-items: center"> <span class="icon-question-answer-outlined grey-700"></span> <p style="font-size: 20px; padding-left: 16px" class="subtitle1"> ${t('wizard.addMessage.responses')}</p></div>`,
                        },
                    },
                    {
                        type: 'form.panel',
                        style: {
                            horizontalAlign: 'right',
                            paddingLeft: config.mode === 'edit' ? 56 : 49,
                        },
                        children: [
                            {
                                type: 'form.panel',
                                style: {
                                    layout: 'column',
                                },
                                children: [
                                    {
                                        type: 'form.checkbox',
                                        key: 'allowResponse',
                                        label: t('wizard.addMessage.allowResponses'),
                                        defaultValue: true,
                                        style: {
                                            type: 'switch',
                                        },
                                        event: {
                                            onUpdateAny: (handler) => {
                                                var _a;
                                                if (handler.config.key === 'allowResponse' && ((_a = handler.formData) === null || _a === void 0 ? void 0 : _a.deliveryOrder)) {
                                                    if (handler.formData.deliveryOrder.deliveryMode === 'escalation') {
                                                        if (!handler.formData.allowResponse) {
                                                            handler.formData.allowResponse = true;
                                                            handler.renderForm({
                                                                changed: true,
                                                                autoSaveImmediately: true,
                                                            });
                                                        }
                                                        if (!handler.config.disabled) {
                                                            handler.setDisabled(true);
                                                        }
                                                    }
                                                    else if (handler.config.disabled) {
                                                        handler.setDisabled(false);
                                                    }
                                                }
                                            },
                                            onChange: (value, handler) => {
                                                const responseOptions = handler.up().down('responseOptions');
                                                if (value === false && handler.formData.responseOptions) {
                                                    handler.formData.responseOptions = [];
                                                    responseOptions === null || responseOptions === void 0 ? void 0 : responseOptions.renderForm({
                                                        changed: true,
                                                        autoSaveImmediately: true,
                                                    });
                                                }
                                                else if (value === true && !handler.formData.responseOptions.length) {
                                                    handler.formData.responseOptions = [
                                                        { text: settings === null || settings === void 0 ? void 0 : settings.notificationOptions.responseOption },
                                                    ];
                                                    responseOptions === null || responseOptions === void 0 ? void 0 : responseOptions.renderForm({
                                                        changed: true,
                                                        autoSaveImmediately: true,
                                                    });
                                                }
                                            },
                                        },
                                    },
                                    {
                                        type: 'form.content',
                                        key: '!responsesInfoMessage',
                                        render: () => {
                                            return (React.createElement(Alert, { severity: 'info' },
                                                React.createElement(Typography, { variant: 'inherit', sx: { fontWeight: 400 } }, t('wizard.addMessage.responsesMustBeAllowedWhenUsingEscalationMessage'))));
                                        },
                                        hidden: {
                                            custom: (handler) => {
                                                var _a;
                                                if (handler.formData.allowResponse &&
                                                    ((_a = handler.formData) === null || _a === void 0 ? void 0 : _a.deliveryOrder) &&
                                                    handler.formData.deliveryOrder.deliveryMode === 'escalation') {
                                                    return false;
                                                }
                                                return true;
                                            },
                                        },
                                    },
                                    {
                                        type: 'form.dataGrid',
                                        key: 'responseOptions',
                                        addButtonLabel: t('wizard.addMessage.addResponse'),
                                        hidden: {
                                            when: '^allowResponse',
                                            dataEq: false,
                                        },
                                        validate: {
                                            minLength: 1,
                                        },
                                        children: [
                                            {
                                                type: 'form.panel',
                                                children: [
                                                    {
                                                        type: 'form.text',
                                                        key: 'text',
                                                        label: t('wizard.addMessage.responseOptions'),
                                                        appendRowIndexToLabel: true,
                                                        validate: {
                                                            required: true,
                                                            requiredMessage: t('common.required'),
                                                        },
                                                    },
                                                    {
                                                        type: 'form.panel',
                                                        children: [
                                                            {
                                                                type: 'form.phone',
                                                                key: 'conferenceBridgeNumber',
                                                                label: t('wizard.addMessage.conferenceBridge'),
                                                            },
                                                            {
                                                                type: 'form.text',
                                                                key: 'conferenceBridgeMeetingId',
                                                                label: t('wizard.addMessage.meetingId'),
                                                                validate: {
                                                                    regex: /^[0-9\s*#+]+$/i,
                                                                    regexMessage: t('wizard.addMessage.meetingIdValidationErrorMessage'),
                                                                },
                                                            },
                                                        ],
                                                        style: {
                                                            layout: 'responsive-sm',
                                                            horizontalAlign: 'left',
                                                        },
                                                    },
                                                ],
                                                style: {
                                                    layout: 'responsive-lg',
                                                    horizontalAlign: 'left',
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        type: 'alert.customLayout',
                                        rootSx: {
                                            width: {
                                                xs: '100%',
                                                sm: '98%',
                                                md: '96%',
                                                lg: 'calc(50% - 8px)',
                                                xl: 'calc(50% - 8px)',
                                            },
                                        },
                                        children: [
                                            {
                                                type: 'form.select',
                                                key: 'validityInHours',
                                                label: t('wizard.addMessage.availableForResponse'),
                                                defaultValue: notificationOptions && (notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.availableForResponse)
                                                    ? notificationOptions.availableForResponse.default
                                                    : defaultNotificationOptionsSettingValue.availableForResponse.default,
                                                values: [
                                                    ...Array(notificationOptions && (notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.availableForResponse)
                                                        ? notificationOptions.availableForResponse.max
                                                        : defaultNotificationOptionsSettingValue.availableForResponse.max).keys(),
                                                ].map((key) => ({
                                                    value: key + 1, // Start enumerating from 1, not 0
                                                    label: t('wizard.addMessage.hours', { hour: key + 1 }),
                                                })),
                                                validate: {
                                                    required: true,
                                                    requiredMessage: t('common.required'),
                                                    custom: (value, handler) => {
                                                        const { deliveryOrder, channels, voiceOptions } = handler.formData;
                                                        const repeatCallsMinutes = channels && channels.includes('voice')
                                                            ? voiceOptions.repeatTimes * voiceOptions.waitTime
                                                            : 0;
                                                        if (deliveryOrder && deliveryOrder.deliveryMode === 'escalation') {
                                                            const delayHours = (repeatCallsMinutes +
                                                                deliveryOrder.textDelay +
                                                                deliveryOrder.voiceDelay) /
                                                                60;
                                                            if (delayHours >= handler.formData.validityInHours) {
                                                                return t('pages.edit.errors.ALERT_VALIDITY_SHORTER_THAN_DELIVERY');
                                                            }
                                                        }
                                                        return undefined;
                                                    },
                                                },
                                                hidden: {
                                                    when: '^^/allowResponse',
                                                    dataEq: false,
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ], config.mode),
            ],
        },
    ];
}
function pasteShortMessage(handler, pasteTo, t) {
    var _a;
    let shortMessageData = (_a = handler.up().up().down('textMessage')) === null || _a === void 0 ? void 0 : _a.getData();
    const targetMessageField = handler.up().up().down(pasteTo);
    const currentTargetMessageData = targetMessageField === null || targetMessageField === void 0 ? void 0 : targetMessageField.getData();
    if (pasteTo === 'htmlMessage') {
        shortMessageData = shortMessageData.replace(/^ +/, (match) => match.replace(/ /g, '&nbsp;'));
        shortMessageData = shortMessageData.replace(/ (?= )/g, '&nbsp;');
    }
    if (!currentTargetMessageData || currentTargetMessageData === '') {
        targetMessageField === null || targetMessageField === void 0 ? void 0 : targetMessageField.setData(shortMessageData);
        targetMessageField === null || targetMessageField === void 0 ? void 0 : targetMessageField.renderForm({
            changed: true,
            autoSaveImmediately: true,
        });
    }
    else {
        showFormDialog({
            title: t('wizard.addMessage.pasteShortMessage'),
            config: {
                type: 'form',
                children: [
                    {
                        type: 'form.content',
                        text: t('wizard.addMessage.pasteShortMessageWarning'),
                    },
                ],
            },
            submitLabel: t('wizard.addMessage.pasteMessage'),
            onSubmit: () => __awaiter(this, void 0, void 0, function* () {
                targetMessageField === null || targetMessageField === void 0 ? void 0 : targetMessageField.setData(shortMessageData);
                targetMessageField === null || targetMessageField === void 0 ? void 0 : targetMessageField.renderForm({
                    changed: true,
                    autoSaveImmediately: true,
                });
            }),
        });
    }
}
const ChannelLabelWithIcon = (handler, channel) => {
    const { t } = useAlertUiTranslation();
    const channels = getMatchingChannels(handler);
    const channelExist = channels.includes(channel);
    return (channelExist && (React.createElement(Typography, { variant: 'body1', "data-test": `${channel}-label`, sx: { pl: 1, display: 'flex', alignItems: 'center', fontSize: '14px', color: themeColors.text.subtle } },
        React.createElement(Box, { component: 'span', sx: { display: 'flex', pr: 1 } },
            React.createElement(SvgIcon, { sx: { fontSize: 20 }, "data-testid": 'test' }, channelIcon[channel])),
        channelText(channel, t))));
};
function getMatchingChannels(handler) {
    var _a, _b;
    const formDataChannels = new Set(((_a = handler.formData) === null || _a === void 0 ? void 0 : _a.channels) || []);
    const formParamsChannels = new Set(((_b = handler.formParams) === null || _b === void 0 ? void 0 : _b.availableChannels) || []);
    const matchingChannels = [...formDataChannels].filter((channel) => formParamsChannels.has(channel));
    if (formDataChannels.has('inbox')) {
        matchingChannels.push('inbox');
    }
    return matchingChannels;
}
